import React, { useState, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Tooltip,
  Typography,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CreateIcon from '@mui/icons-material/Create';
import TimerIcon from '@mui/icons-material/Timer';
import PieChartIcon from '@mui/icons-material/PieChart';
import { formatAudioDuration } from '@/components/util';
import { RefreshIconButton } from '@/components/util/RefreshIconButton';
import { TherapistRecordingView } from '../therapistRecordings/TherapistRecordingView';
import {
  RecordingStatusSemaphore,
  statusConfig,
} from './RecordingStatusSemaphore';
import { RecordingStatusBar } from './RecordingStatusBar';

interface TherapistInputSourcesAccordionProps {
  inputSources: any[];
  recordings: any[];
  isFetching: boolean;
}

export const TherapistInputSourcesAccordion: React.FC<
  TherapistInputSourcesAccordionProps
> = ({ inputSources, recordings, isFetching }) => {
  const [expandedMain, setExpandedMain] = useState(false);
  const [expandedSources, setExpandedSources] = useState<
    Record<string, boolean>
  >({});

  const handleMainAccordionChange = useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedMain(isExpanded);
    },
    [],
  );

  const handleSourceAccordionChange = useCallback(
    (sourceId: string) =>
      (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedSources(prev => ({
          ...prev,
          [sourceId]: isExpanded,
        }));
      },
    [],
  );

  const inputSourcesWithStats = useMemo(() => {
    if (!recordings || !inputSources) {
      return [];
    }

    const reversedInputSources = [...inputSources].reverse();

    return reversedInputSources.map(source => {
      // Extract filename from URI (browser-compatible)
      const fileName = source.fileUri.split('/').pop() || source.fileUri.split('\\').pop() || source.fileUri;

      // Find recordings associated with this input source
      const sourceRecordings = recordings.filter(rec =>
        rec.localFileSystemUri?.includes(fileName),
      );

      console.log('SOURCE RECORDINGS', sourceRecordings);

      // Calculate status distribution
      const statusDistribution = sourceRecordings.reduce(
        (acc: any, rec: any) => {
          const status = rec.status || 'unknown';
          acc[status] = (acc[status] || 0) + rec.duration;
          return acc;
        },
        {},
      );

      console.log('STATUS DISTRIBUTION', statusDistribution);

      // Calculate coverage (how much of the input source duration is covered by recordings)
      const totalRecordedDuration = sourceRecordings.reduce(
        (sum: number, recording: any) => sum + recording.duration,
        0,
      );

      console.log('TOTAL RECORDED DURATION', totalRecordedDuration);

      // Round the coverage to handle floating point precision
      const rawCoverage =
        source.durationMs > 0
          ? (totalRecordedDuration / source.durationMs) * 100
          : 0;

      // If coverage is very close to 100%, round it up to 100
      const coverage =
        rawCoverage > 99.5 ? 100 : Math.min(100, rawCoverage);

      return {
        ...source,
        fileName,
        statusDistribution,
        totalRecordedDuration,
        coverage,
        isActive: !source.endDate,
        recordings: sourceRecordings,
      };
    });
  }, [recordings, inputSources]);

  const totalInputSourcesDuration = useMemo(() => {
    if (!inputSources) {
      return 0;
    }
    return inputSources.reduce(
      (sum, source) => sum + source.durationMs,
      0,
    );
  }, [inputSources]);

  // Add this new useMemo to calculate combined status distribution
  const combinedStatusDistribution = useMemo(() => {
    if (!inputSourcesWithStats.length) {
      return {};
    }

    // Initialize combined distribution
    const combined: Record<string, number> = {};

    // Sum up durations for each status across all input sources
    inputSourcesWithStats.forEach(source => {
      Object.entries(source.statusDistribution).forEach(
        ([status, duration]) => {
          combined[status] =
            (combined[status] || 0) + Number(duration);
        },
      );
    });

    return combined;
  }, [inputSourcesWithStats]);

  // Calculate total coverage
  const totalCoverage = useMemo(() => {
    if (!totalInputSourcesDuration) {
      return 0;
    }

    const totalRecordedDuration = Object.values(
      combinedStatusDistribution,
    ).reduce(
      (sum: number, duration: any) => sum + Number(duration),
      0,
    );

    const rawCoverage =
      (totalRecordedDuration / totalInputSourcesDuration) * 100;

    return Math.min(100, rawCoverage > 99.5 ? 100 : rawCoverage);
  }, [combinedStatusDistribution, totalInputSourcesDuration]);

  return (
    <Accordion
      expanded={expandedMain}
      onChange={handleMainAccordionChange}
      sx={{
        marginTop: 2,
        width: '100%',
        maxWidth: '1200px',
        '&.MuiAccordion-root': {
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
        '& .MuiAccordionSummary-root': {
          padding: '0 8px 0 0',
          minHeight: 'auto',
          flexDirection: 'row-reverse',
          maxWidth: '1200px',
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
          marginRight: 1,
        },
        '& .MuiAccordionDetails-root': {
          padding: '8px 0 16px',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="input-sources-content"
        id="input-sources-header"
        sx={{
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            alignItems: 'center',
            flexDirection: 'column',
            width: '960px',
            // maxWidth: "1960px",
          },
        }}
      >
        <Box display="flex" width="100%" alignItems="center" mb={1}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 500 }}
          >
            Source Files
          </Typography>

          <RefreshIconButton isFetching={isFetching} />

          <Box display="flex" ml={2} alignItems="center">
            <Tooltip
              title={`Total Duration: ${formatAudioDuration(
                totalInputSourcesDuration,
              )}`}
            >
              <Chip
                icon={<AssessmentIcon />}
                label={`${inputSources?.length || 0} files`}
                sx={{
                  mx: 0.5,
                  color: 'white',
                  backgroundColor: '#607D8B',
                  '& .MuiChip-icon': {
                    color: 'white',
                    marginLeft: '.4rem',
                    fontSize: '1.2rem',
                  },
                  '& .MuiChip-label': {
                    padding: '0 10px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                  },
                  height: '28px',
                  borderRadius: '14px',
                }}
                size="medium"
                onClick={e => e.stopPropagation()}
              />
            </Tooltip>

            {inputSources?.some(source => !source.endDate) && (
              <Tooltip title="Recording in progress">
                <Chip
                  icon={<CreateIcon />}
                  label="Active"
                  sx={{
                    mx: 0.5,
                    backgroundColor: '#F44336',
                    color: 'white',
                    '& .MuiChip-icon': {
                      color: 'white',
                      marginLeft: '.4rem',
                      fontSize: '1.2rem',
                    },
                    '& .MuiChip-label': {
                      padding: '0 10px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                    },
                    height: '28px',
                    borderRadius: '14px',
                  }}
                  size="medium"
                  onClick={e => e.stopPropagation()}
                />
              </Tooltip>
            )}

            <RecordingStatusSemaphore recordings={recordings} />
          </Box>
        </Box>

        {/* Add combined progress bar in header */}
        {inputSourcesWithStats.length > 0 && (
          <RecordingStatusBar
            statusDistribution={combinedStatusDistribution}
            totalDuration={totalInputSourcesDuration}
          />
        )}
      </AccordionSummary>

      <AccordionDetails>
        {inputSourcesWithStats.length === 0 && (
          <Typography color="textSecondary" sx={{ padding: 2 }}>
            No input sources found for this session note.
          </Typography>
        )}

        {inputSourcesWithStats.map((source, index) => {
          const sourceId = source.id || index.toString();

          return (
            <Accordion
              key={index}
              expanded={
                !!expandedSources[source.id || index.toString()]
              }
              onChange={handleSourceAccordionChange(
                source.id || index.toString(),
              )}
              sx={{
                mb: 2,
                border: source.isActive
                  ? '1px solid #F44336'
                  : 'none',
                '&::before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`source-content-${index}`}
                id={`source-header-${index}`}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                      >
                        {source.fileName}
                        {source.isActive && (
                          <Chip
                            label="Active"
                            size="small"
                            color="error"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Box marginLeft="1rem">
                        <Tooltip
                          title={`Duration: ${formatAudioDuration(
                            source.durationMs,
                          )}`}
                        >
                          <Chip
                            icon={<TimerIcon />}
                            label={formatAudioDuration(
                              source.durationMs,
                            )}
                            sx={{
                              mx: 0.5,
                              backgroundColor: '#607D8B',
                              color: 'white',
                              '& .MuiChip-icon': {
                                color: 'white',
                                marginLeft: '.4rem',
                                fontSize: '1.2rem',
                              },
                              '& .MuiChip-label': {
                                padding: '0 10px',
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                              },
                              height: '28px',
                              borderRadius: '14px',
                            }}
                          />
                        </Tooltip>

                        <Tooltip
                          title={`Coverage: ${source.coverage.toFixed(1)}%`}
                        >
                          <Chip
                            icon={<PieChartIcon />}
                            label={`${source.coverage}%`}
                            sx={{
                              mx: 0.5,
                              backgroundColor:
                                source.coverage === 100
                                  ? statusConfig.transcribed.color
                                  : '#455A64',
                              color: 'white',
                              '& .MuiChip-icon': {
                                color: 'white',
                                marginLeft: '.4rem',
                                fontSize: '1.2rem',
                              },
                              '& .MuiChip-label': {
                                padding: '0 10px',
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                              },
                              height: '28px',
                              borderRadius: '14px',
                            }}
                          />
                        </Tooltip>

                        <RecordingStatusSemaphore
                          recordings={source.recordings}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <RecordingStatusBar
                      statusDistribution={source.statusDistribution}
                      totalDuration={source.durationMs}
                      height={20}
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>

              <AccordionDetails>
                <Box paddingLeft="2.4rem">
                  <>
                    {source.recordings.length === 0 ? (
                      <Typography color="textSecondary">
                        No recordings found for this source file.
                      </Typography>
                    ) : (
                      <>
                        {source.recordings.map(
                          (recording: any, recIndex: number) => (
                            <TherapistRecordingView
                              key={recording.id}
                              recording={recording}
                            />
                          ),
                        )}
                      </>
                    )}
                  </>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};
